<template>
  <div class="platform-billing-container">
    <div class="header">
      <div>
        <h2>Platform Billing</h2>
        <div class="subtitle">Manage renewal, unpaid invoices, refunds, and access rollback with separate audited actions.</div>
      </div>
      <el-button size="small" icon="el-icon-refresh" @click="refresh">Refresh</el-button>
    </div>

    <el-form :inline="true" size="small" class="filters" @submit.native.prevent="search">
      <el-form-item label="User">
        <el-input
          v-model="filters.search"
          clearable
          placeholder="Email, user id, Stripe id"
          style="width: 320px"
          @clear="search"
          @keyup.enter.native="search"
        >
          <el-button slot="append" icon="el-icon-search" @click="search" />
        </el-input>
      </el-form-item>
    </el-form>

    <el-table
      v-loading="subscriptionLoading"
      :data="subscriptions"
      border
      size="small"
      style="width: 100%"
      :row-class-name="subscriptionRowClass"
      @row-click="selectSubscription"
    >
      <el-table-column label="User" min-width="240">
        <template slot-scope="scope">
          <div class="user-main">{{ scope.row.user_email || '-' }}</div>
          <div class="muted">{{ scope.row.user_name || scope.row.user_id || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Plan" width="120">
        <template slot-scope="scope">
          <el-tag size="small" type="info">{{ scope.row.plan_key || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="120">
        <template slot-scope="scope">
          <el-tag size="small" :type="statusTagType(scope.row.status)">
            {{ scope.row.status || '-' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Payment Identity" width="145">
        <template slot-scope="scope">
          <el-tag size="small" :type="scope.row.payment_identity_action_required ? 'warning' : 'info'">
            {{ scope.row.payment_identity_action_required ? 'Needs correction' : (scope.row.stripe_payment_identity_verification_status || 'Not checked') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Access Until" width="160">
        <template slot-scope="scope">{{ formatDate(scope.row.end_date) }}</template>
      </el-table-column>
      <el-table-column label="Auto Renew" width="110">
        <template slot-scope="scope">
          <el-tag size="small" :type="scope.row.auto_renew ? 'success' : 'info'">
            {{ scope.row.auto_renew ? 'Enabled' : 'Off' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Stripe Subscription" min-width="230">
        <template slot-scope="scope">
          <span class="mono">{{ scope.row.stripe_subscription_id || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="245" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" @click.stop="selectSubscription(scope.row)">Billing</el-button>
          <el-button v-if="scope.row.stripe_subscription_id" size="mini" @click.stop="openIdentityCheck(scope.row)">Check Stripe</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :page-sizes="[10, 20, 50]"
        :total="total"
        @size-change="fetchSubscriptions"
        @current-change="fetchSubscriptions"
      />
    </div>

    <el-dialog title="Check current Stripe payment details" :visible.sync="identityVisible" width="640px" :close-on-click-modal="false">
      <div v-loading="identityLoading">
        <p>Access requires the current Stripe payment email to match the account or registered cardholder email. Names are informational.</p>
        <el-alert v-if="identityError" :title="identityError" type="error" :closable="false" />
        <el-descriptions v-if="identityResult" :column="1" border size="small">
          <el-descriptions-item label="Account email">{{ identityResult.verification.expected.allowed_emails.account_user && identityResult.verification.expected.allowed_emails.account_user.raw || '-' }}</el-descriptions-item>
          <el-descriptions-item label="Registered cardholder">{{ identityResult.verification.expected.allowed_emails.authorized_cardholder && identityResult.verification.expected.allowed_emails.authorized_cardholder.raw || '-' }}</el-descriptions-item>
          <el-descriptions-item label="Current Stripe payment email">{{ identityResult.identity.current_payment_method_email || identityResult.identity.stripe_customer_email || '-' }}</el-descriptions-item>
          <el-descriptions-item label="Result">{{ identityResult.verification.email_status }} · {{ identityResult.verification.user_correction_required ? 'Access blocked' : 'Identity check passed' }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <span slot="footer">
        <el-button @click="identityVisible = false">Close</el-button>
        <el-button :loading="identityLoading" @click="checkIdentity(false)">Recheck</el-button>
        <el-button type="primary" :disabled="!identityResult || identityLoading" @click="checkIdentity(true)">Apply current Stripe check</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Billing History"
      :visible.sync="historyVisible"
      width="calc(100% - 32px)"
      class="history-dialog"
    >
      <div v-if="historyUser" class="history-panel">
        <div class="panel-header">
          <div>
            <h3>{{ historyUser.user_email || '-' }}</h3>
            <div class="subtitle">
              Active until {{ formatDate(activeSubscription && activeSubscription.end_date) }}
              <span v-if="activeSubscription"> · {{ activeSubscription.status }} · {{ activeSubscription.plan_key }}</span>
            </div>
          </div>
          <div class="history-actions">
            <el-button
              size="small"
              type="danger"
              :loading="combinedLoading"
              :disabled="historyLoading || !canRunCombinedAction(combinedCandidate)"
              @click="openCombinedAction(combinedCandidate)"
            >
              Cancel + Void + Refund + Rollback
            </el-button>
            <el-button
              size="small"
              type="warning"
              :disabled="!canCancelRenewal(billingSubscription)"
              @click="openCancelRenewal(billingSubscription)"
            >
              Cancel Only
            </el-button>
            <el-button
              size="small"
              type="warning"
              :disabled="!rollbackCandidate"
              @click="openRollback(rollbackCandidate)"
            >
              Rollback Only
            </el-button>
            <el-button size="small" icon="el-icon-refresh" :loading="historyLoading" @click="loadHistory(historyUser._id)">Reload History</el-button>
          </div>
        </div>

        <div class="time-context">
          <i class="el-icon-time" aria-hidden="true" />
          Times use {{ browserTimeZoneLabel }}. UTC is shown underneath for bank reconciliation.
        </div>

        <el-table
          v-loading="historyLoading"
          :data="history"
          border
          size="small"
          style="width: 100%"
        >
          <el-table-column label="Invoice Created" width="190">
            <template slot-scope="scope">
              <div class="time-primary">{{ formatDate(getInvoiceCreatedAt(scope.row), true) }}</div>
              <div class="muted tiny time-utc">{{ formatUtcDate(getInvoiceCreatedAt(scope.row)) }}</div>
              <div v-if="scope.row.source !== 'stripe_invoice'" class="muted tiny">Record created</div>
            </template>
          </el-table-column>
          <el-table-column label="Invoice" min-width="230">
            <template slot-scope="scope">
              <div class="mono">{{ scope.row.stripe_invoice_id || scope.row.source || '-' }}</div>
              <div class="muted tiny">{{ scope.row.source || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="Plan" width="110">
            <template slot-scope="scope">{{ scope.row.plan_key || '-' }}</template>
          </el-table-column>
          <el-table-column label="Amount" width="110">
            <template slot-scope="scope">{{ formatMoney(scope.row.usd_price, scope.row.currency) }}</template>
          </el-table-column>
          <el-table-column label="Payment Status / Time" width="220">
            <template slot-scope="scope">
              <div class="payment-heading">
                <el-tag size="mini" :type="paymentStatusTagType(scope.row.status)">
                  {{ paymentStatusLabel(scope.row.status) }}
                </el-tag>
                <span v-if="paymentAttemptLabel(scope.row)" class="attempt-count">
                  {{ paymentAttemptLabel(scope.row) }}
                </span>
              </div>
              <template v-if="getInvoiceStatusEventAt(scope.row)">
                <div class="time-primary">{{ formatDate(getInvoiceStatusEventAt(scope.row), true) }}</div>
                <div class="muted tiny time-utc">{{ formatUtcDate(getInvoiceStatusEventAt(scope.row)) }}</div>
              </template>
              <template v-else-if="scope.row.next_payment_attempt_at">
                <div class="muted tiny payment-detail-label">Next retry</div>
                <div class="time-primary">{{ formatDate(scope.row.next_payment_attempt_at, true) }}</div>
                <div class="muted tiny time-utc">{{ formatUtcDate(scope.row.next_payment_attempt_at) }}</div>
              </template>
              <div v-else class="muted tiny payment-detail-label">No successful payment time</div>
            </template>
          </el-table-column>
          <el-table-column label="Invoice Period" width="260">
            <template slot-scope="scope">
              <div>{{ formatDate(scope.row.period_start) }}</div>
              <div class="muted">to {{ formatDate(scope.row.end_date) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="Admin State" width="180">
            <template slot-scope="scope">
              <div class="state-tags">
                <el-tag v-if="isVoidedInvoice(scope.row)" size="small" type="info">Collection Stopped</el-tag>
                <el-tag v-if="scope.row.refunded" size="small" type="success">{{ refundStateLabel(scope.row) }}</el-tag>
                <el-tag v-else-if="scope.row.can_refund" size="small" type="warning">Refundable</el-tag>
                <el-tag v-if="scope.row.rolled_back" size="small" type="success">Rolled Back</el-tag>
                <el-tag v-else-if="scope.row.can_rollback" size="small" type="warning">Rollback Ready</el-tag>
              </div>
              <div v-if="scope.row.refund_restore_end_date" class="muted tiny">
                rollback to {{ formatDate(scope.row.refund_restore_end_date) }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="210" fixed="right">
            <template slot-scope="scope">
              <div class="invoice-actions">
                <el-button
                  v-if="canVoidInvoice(scope.row)"
                  size="mini"
                  type="danger"
                  plain
                  @click="openVoidInvoice(scope.row)"
                >
                  Void Invoice
                </el-button>
                <template v-else>
                  <el-button
                    size="mini"
                    type="primary"
                    plain
                    :disabled="!canCheckRefundEligibility(scope.row)"
                    @click="openRefundEligibility(scope.row)"
                  >
                    Check Refund Eligibility
                  </el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    :disabled="!scope.row.can_refund"
                    @click="openRefund(scope.row)"
                  >
                    Refund Only
                  </el-button>
                </template>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog
      title="Void Unpaid Invoice"
      :visible.sync="voidVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <div v-if="voidTarget">
        <el-alert
          title="Voiding permanently closes this unpaid Stripe invoice and stops its automatic payment retries."
          description="This cannot be undone. It does not refund money, restore access, or cancel future subscription renewals."
          type="error"
          :closable="false"
          show-icon
          class="refund-alert"
        />

        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="Invoice">
            <span class="mono">{{ voidTarget.stripe_invoice_id }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Amount due">{{ formatMoney(voidTarget.usd_price, voidTarget.currency) }}</el-descriptions-item>
          <el-descriptions-item label="Payment attempts">{{ Number(voidTarget.payment_attempt_count || 0) }}</el-descriptions-item>
          <el-descriptions-item label="Next retry">{{ formatDate(voidTarget.next_payment_attempt_at, true) }}</el-descriptions-item>
          <el-descriptions-item label="Auto renew">
            <el-tag size="small" :type="voidTarget.subscription_auto_renew ? 'warning' : 'info'">
              {{ voidTarget.subscription_auto_renew ? 'Still enabled' : 'Off' }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <el-alert
          v-if="voidTarget.subscription_auto_renew"
          title="Future renewal is still enabled"
          description="Void Invoice only stops this invoice. Use Cancel Only separately if no future invoices should be created."
          type="warning"
          :closable="false"
          show-icon
          class="void-renewal-alert"
        />

        <el-form label-width="120px" size="small" class="refund-form">
          <el-form-item label="Admin note">
            <el-input
              v-model="voidForm.admin_note"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              placeholder="Reason for stopping collection on this unpaid invoice."
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer">
        <el-button :disabled="voidLoading" @click="voidVisible = false">Cancel</el-button>
        <el-button type="danger" :loading="voidLoading" @click="submitVoidInvoice">Confirm Void Invoice</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Cancel, Void Due Invoices, Refund, and Roll Back"
      :visible.sync="combinedVisible"
      width="calc(100% - 32px)"
      class="combined-dialog"
      :close-on-click-modal="false"
    >
      <div v-if="combinedTarget">
        <el-alert
          title="This four-step action stops renewal, permanently voids every OPEN invoice, refunds the selected paid invoice, and rolls local access back."
          description="The server rechecks the Stripe subscription before acting. If any open invoice cannot be voided, the refund and access rollback will not start."
          type="error"
          :closable="false"
          show-icon
          class="refund-alert"
        />

        <section class="combined-plan" aria-labelledby="combined-plan-title">
          <div class="combined-plan-heading">
            <div>
              <h4 id="combined-plan-title">Operations run in this order</h4>
              <p>Completed steps are detected and skipped safely if an interrupted action is retried.</p>
            </div>
            <el-tag size="small" type="danger">4 steps</el-tag>
          </div>
          <ol class="combined-plan-list">
            <li><span class="step-number">1</span><span><strong>Cancel renewal</strong><small>Stop future subscription cycles.</small></span></li>
            <li><span class="step-number">2</span><span><strong>Void OPEN invoices</strong><small>Stop automatic retries and permanently close unpaid hosted invoices.</small></span></li>
            <li><span class="step-number">3</span><span><strong>Refund selected paid invoice</strong><small>{{ combinedTarget.stripe_invoice_id }}</small></span></li>
            <li><span class="step-number">4</span><span><strong>Roll back access</strong><small>Restore the local access date to the selected invoice period start.</small></span></li>
          </ol>
        </section>

        <section class="open-invoice-review" aria-labelledby="open-invoice-title">
          <div class="open-invoice-heading">
            <div>
              <h4 id="open-invoice-title">OPEN invoices currently visible</h4>
              <p>These will be voided before the refund. The server scans all Stripe pages again when confirmed.</p>
            </div>
            <el-tag size="small" :type="combinedOpenInvoices.length ? 'danger' : 'info'">
              {{ combinedOpenInvoices.length }} found
            </el-tag>
          </div>
          <div v-if="combinedOpenInvoices.length" class="open-invoice-list">
            <div
              v-for="invoice in combinedOpenInvoices"
              :key="invoice.stripe_invoice_id"
              class="open-invoice-row"
            >
              <div>
                <div class="mono">{{ invoice.stripe_invoice_id }}</div>
                <div class="muted tiny">
                  {{ paymentAttemptLabel(invoice) || 'No payment attempt yet' }}
                  <span v-if="invoice.next_payment_attempt_at"> · Next retry {{ formatDate(invoice.next_payment_attempt_at, true) }}</span>
                </div>
              </div>
              <strong>{{ formatMoney(invoice.usd_price, invoice.currency) }}</strong>
            </div>
          </div>
          <el-alert
            v-else
            title="No OPEN invoice appears in the currently loaded history."
            description="This step will still run: the server will recheck every Stripe invoice and void any OPEN invoice found before refunding."
            type="info"
            :closable="false"
            show-icon
          />
        </section>

        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="Invoice">
            <span class="mono">{{ combinedTarget.stripe_invoice_id }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Amount">{{ formatMoney(combinedTarget.usd_price, combinedTarget.currency) }}</el-descriptions-item>
          <el-descriptions-item label="Payment succeeded">{{ formatDate(getPaymentEventAt(combinedTarget), true) }}</el-descriptions-item>
          <el-descriptions-item label="Current access until">{{ formatDate(activeSubscription && activeSubscription.end_date) }}</el-descriptions-item>
          <el-descriptions-item label="Roll access back to">{{ formatDate(combinedTarget.refund_restore_end_date || combinedTarget.period_start) }}</el-descriptions-item>
        </el-descriptions>

        <section
          v-if="combinedTarget.can_refund"
          class="refund-policy-panel"
          aria-labelledby="combined-refund-policy-title"
          v-loading="combinedRefundQuoteLoading"
        >
          <div class="refund-policy-heading">
            <div>
              <h4 id="combined-refund-policy-title">Deduct the Stripe processing fee?</h4>
              <p>Stripe's recorded fee is used to calculate the refund automatically.</p>
            </div>
            <el-button
              v-if="combinedRefundQuoteError"
              size="mini"
              icon="el-icon-refresh"
              :loading="combinedRefundQuoteLoading"
              @click="loadCombinedRefundQuote"
            >
              Retry
            </el-button>
          </div>

          <el-alert
            v-if="combinedRefundQuoteError"
            :title="combinedRefundQuoteError"
            description="No billing action can start until Stripe returns a fresh refund calculation."
            type="error"
            :closable="false"
            show-icon
          />
          <template v-else-if="combinedRefundQuote">
            <el-radio-group v-model="combinedForm.refund_policy" class="refund-policy-options">
              <el-radio label="full" border class="refund-policy-option">
                <span class="refund-policy-label">No — do not deduct the fee</span>
                <span class="refund-policy-description">Refund the full remaining payment</span>
              </el-radio>
              <el-radio
                label="deduct_stripe_fee"
                border
                class="refund-policy-option"
                :disabled="!combinedRefundQuote.fee_deduction_available"
              >
                <span class="refund-policy-label">Yes — deduct the fee</span>
                <span class="refund-policy-description">Subtract Stripe's actual fee from the refund</span>
              </el-radio>
            </el-radio-group>

            <div class="refund-calculation" aria-live="polite">
              <div><span>Customer paid</span><strong>{{ formatCents(combinedRefundQuote.invoice_amount_cents, combinedRefundQuote.currency) }}</strong></div>
              <div v-if="combinedRefundQuote.already_refunded_cents"><span>Previously refunded</span><strong>{{ formatCents(combinedRefundQuote.already_refunded_cents, combinedRefundQuote.currency) }}</strong></div>
              <div><span>Stripe processing fee</span><strong>{{ formatOptionalCents(combinedRefundQuote.processing_fee_cents, combinedRefundQuote.processing_fee_currency || combinedRefundQuote.currency) }}</strong></div>
              <div
                v-if="combinedForm.refund_policy === 'deduct_stripe_fee' && combinedRefundQuote.fee_deduction_available && combinedRefundQuote.processing_fee_currency !== combinedRefundQuote.currency"
              ><span>Fee deducted from refund</span><strong>{{ formatOptionalCents(combinedRefundQuote.processing_fee_deduction_cents, combinedRefundQuote.currency) }}</strong></div>
              <div class="refund-total"><span>Refund to customer</span><strong>{{ formatCents(refundAmountForPolicy(combinedRefundQuote, combinedForm.refund_policy), combinedRefundQuote.currency) }}</strong></div>
            </div>
            <p v-if="!combinedRefundQuote.fee_deduction_available" class="refund-policy-note">
              Fee deduction is unavailable: {{ refundFeeUnavailableMessage(combinedRefundQuote.fee_deduction_unavailable_reason) }}
            </p>
            <p v-else class="refund-policy-note">{{ refundFeePolicyMessage(combinedForm.refund_policy) }}</p>
          </template>
          <div v-else class="refund-quote-placeholder">Loading the actual Stripe fee and refundable amount…</div>
        </section>
        <el-alert
          v-else-if="combinedTarget.refunded"
          title="The refund step was already completed"
          :description="existingRefundDescription(combinedTarget)"
          type="success"
          :closable="false"
          show-icon
          class="already-refunded-alert"
        />

        <section class="usage-review" v-loading="combinedUsageLoading">
          <div class="usage-review-heading">
            <div>
              <h4>LearningFirst usage after payment</h4>
              <p>The reading activity log is checked before the administrator chooses an action.</p>
            </div>
            <el-button
              v-if="combinedUsageError"
              size="mini"
              icon="el-icon-refresh"
              :loading="combinedUsageLoading"
              @click="loadCombinedUsage"
            >
              Check again
            </el-button>
          </div>

          <el-alert
            v-if="combinedUsageError"
            :title="combinedUsageError"
            description="This is an advisory check only. You can still continue after verifying the activity manually."
            type="warning"
            :closable="false"
            show-icon
          />
          <template v-else-if="combinedUsage">
            <el-alert
              v-if="combinedUsage.has_usage"
              title="This student has used LearningFirst after the charge."
              :description="usageAlertDescription(combinedUsage)"
              type="warning"
              :closable="false"
              show-icon
            />
            <el-alert
              v-else
              title="No reading activity was found after the charge."
              description="The current LearningFirst reading activity log contains no event at or after the payment time."
              type="success"
              :closable="false"
              show-icon
            />

            <div v-if="combinedUsage.has_usage && combinedUsage.recent_activity.length" class="usage-events">
              <div
                v-for="(activity, index) in combinedUsage.recent_activity"
                :key="`${activity.post_id || 'activity'}-${activity.read_time}-${index}`"
                class="usage-event"
              >
                <span class="usage-event-time">{{ formatDate(activity.read_time, true) }}</span>
                <span class="usage-event-title">{{ activity.post_title || activity.collection_title || 'Learning activity' }}</span>
                <span class="usage-event-scene">{{ activity.read_scene || 'reading' }}</span>
              </div>
            </div>
          </template>
          <div v-else class="usage-check-placeholder">
            Checking the student's reading activity after payment…
          </div>
        </section>

        <el-form label-width="120px" size="small" class="refund-form">
          <el-form-item label="Admin note">
            <el-input
              v-model="combinedForm.admin_note"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              placeholder="Reason for cancellation, invoice voiding, refund, and access rollback."
            />
          </el-form-item>
        </el-form>

        <div class="customer-notice">
          <i class="el-icon-message" aria-hidden="true" />
          A customer email will be sent after cancellation, invoice voiding, refund, and access rollback all succeed.
        </div>
      </div>

      <span slot="footer">
        <el-button :disabled="combinedLoading" @click="combinedVisible = false">Cancel</el-button>
        <el-button
          type="danger"
          :loading="combinedLoading"
          :disabled="combinedSubmitDisabled"
          @click="submitCombinedAction"
        >
          {{ combinedActionButtonLabel }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Refund Eligibility Check"
      :visible.sync="refundEligibilityVisible"
      width="700px"
      :close-on-click-modal="false"
      @closed="resetRefundEligibilityCheck"
    >
      <div v-if="refundEligibilityTarget">
        <el-alert
          title="This check only reviews LearningFirst activity after payment. It does not refund, cancel, or change access."
          type="info"
          :closable="false"
          show-icon
          class="refund-alert"
        />

        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="Invoice">
            <span class="mono">{{ refundEligibilityTarget.stripe_invoice_id }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Amount">
            {{ formatMoney(refundEligibilityTarget.usd_price, refundEligibilityTarget.currency) }}
          </el-descriptions-item>
          <el-descriptions-item label="Payment succeeded">
            {{ formatDate(getPaymentEventAt(refundEligibilityTarget), true) }}
          </el-descriptions-item>
        </el-descriptions>

        <section class="usage-review" v-loading="refundEligibilityLoading">
          <div class="usage-review-heading">
            <div>
              <h4>LearningFirst usage after payment</h4>
              <p>The result is advisory and does not block an administrator from refunding.</p>
            </div>
            <el-button
              v-if="refundEligibilityError"
              size="mini"
              icon="el-icon-refresh"
              :loading="refundEligibilityLoading"
              @click="loadRefundEligibility"
            >
              Check again
            </el-button>
          </div>

          <el-alert
            v-if="refundEligibilityError"
            :title="refundEligibilityError"
            description="The eligibility result is unavailable. You can retry or verify the activity manually."
            type="warning"
            :closable="false"
            show-icon
          />
          <template v-else-if="refundEligibilityUsage">
            <el-alert
              v-if="refundEligibilityUsage.has_usage"
              title="Review required: activity was found after payment."
              :description="refundEligibilityDescription(refundEligibilityUsage)"
              type="warning"
              :closable="false"
              show-icon
            />
            <el-alert
              v-else
              title="Refund eligibility check passed."
              description="No LearningFirst reading activity was found at or after the successful payment time."
              type="success"
              :closable="false"
              show-icon
            />

            <div
              v-if="refundEligibilityUsage.has_usage && refundEligibilityUsage.recent_activity.length"
              class="usage-events"
            >
              <div
                v-for="(activity, index) in refundEligibilityUsage.recent_activity"
                :key="`${activity.post_id || 'activity'}-${activity.read_time}-${index}`"
                class="usage-event"
              >
                <span class="usage-event-time">{{ formatDate(activity.read_time, true) }}</span>
                <span class="usage-event-title">{{ activity.post_title || activity.collection_title || 'Learning activity' }}</span>
                <span class="usage-event-scene">{{ activity.read_scene || 'reading' }}</span>
              </div>
            </div>
          </template>
          <div v-else class="usage-check-placeholder">
            Checking the student's reading activity after payment…
          </div>
        </section>

        <div class="eligibility-note">
          <i class="el-icon-info" aria-hidden="true" />
          Stripe refund availability is shown separately by the Refundable state and Refund Only button.
        </div>
      </div>

      <span slot="footer">
        <el-button
          icon="el-icon-refresh"
          :loading="refundEligibilityLoading"
          @click="loadRefundEligibility"
        >
          Check Again
        </el-button>
        <el-button type="primary" @click="refundEligibilityVisible = false">Close</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Refund Subscription Invoice"
      :visible.sync="refundVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <div v-if="refundTarget">
        <el-alert
          title="This action only refunds this invoice in Stripe. It does not cancel renewal or change local access."
          type="warning"
          :closable="false"
          show-icon
          class="refund-alert"
        />

        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="Invoice">
            <span class="mono">{{ refundTarget.stripe_invoice_id }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Amount">{{ formatMoney(refundTarget.usd_price, refundTarget.currency) }}</el-descriptions-item>
          <el-descriptions-item label="Invoice created">{{ formatDate(getInvoiceCreatedAt(refundTarget), true) }}</el-descriptions-item>
          <el-descriptions-item label="Payment succeeded">{{ formatDate(getPaymentEventAt(refundTarget), true) }}</el-descriptions-item>
          <el-descriptions-item label="Current access until">{{ formatDate(activeSubscription && activeSubscription.end_date) }}</el-descriptions-item>
        </el-descriptions>

        <section
          class="refund-policy-panel"
          aria-labelledby="refund-policy-title"
          v-loading="refundQuoteLoading"
        >
          <div class="refund-policy-heading">
            <div>
              <h4 id="refund-policy-title">Deduct the Stripe processing fee?</h4>
              <p>Default: do not deduct the fee; refund the full remaining payment.</p>
            </div>
            <el-button
              v-if="refundQuoteError"
              size="mini"
              icon="el-icon-refresh"
              :loading="refundQuoteLoading"
              @click="loadRefundQuote"
            >
              Retry
            </el-button>
          </div>

          <el-alert
            v-if="refundQuoteError"
            :title="refundQuoteError"
            description="Refund is disabled until Stripe returns a fresh calculation."
            type="error"
            :closable="false"
            show-icon
          />
          <template v-else-if="refundQuote">
            <el-radio-group v-model="refundForm.refund_policy" class="refund-policy-options">
              <el-radio label="full" border class="refund-policy-option">
                <span class="refund-policy-label">No — do not deduct the fee</span>
                <span class="refund-policy-description">Refund the full remaining payment</span>
              </el-radio>
              <el-radio
                label="deduct_stripe_fee"
                border
                class="refund-policy-option"
                :disabled="!refundQuote.fee_deduction_available"
              >
                <span class="refund-policy-label">Yes — deduct the fee</span>
                <span class="refund-policy-description">Subtract Stripe's actual fee from the refund</span>
              </el-radio>
            </el-radio-group>

            <div class="refund-calculation" aria-live="polite">
              <div><span>Customer paid</span><strong>{{ formatCents(refundQuote.invoice_amount_cents, refundQuote.currency) }}</strong></div>
              <div v-if="refundQuote.already_refunded_cents"><span>Previously refunded</span><strong>{{ formatCents(refundQuote.already_refunded_cents, refundQuote.currency) }}</strong></div>
              <div><span>Stripe processing fee</span><strong>{{ formatOptionalCents(refundQuote.processing_fee_cents, refundQuote.processing_fee_currency || refundQuote.currency) }}</strong></div>
              <div
                v-if="refundForm.refund_policy === 'deduct_stripe_fee' && refundQuote.fee_deduction_available && refundQuote.processing_fee_currency !== refundQuote.currency"
              ><span>Fee deducted from refund</span><strong>{{ formatOptionalCents(refundQuote.processing_fee_deduction_cents, refundQuote.currency) }}</strong></div>
              <div class="refund-total"><span>Refund to customer</span><strong>{{ formatCents(refundAmountForPolicy(refundQuote, refundForm.refund_policy), refundQuote.currency) }}</strong></div>
            </div>
            <p v-if="!refundQuote.fee_deduction_available" class="refund-policy-note">
              Fee deduction is unavailable: {{ refundFeeUnavailableMessage(refundQuote.fee_deduction_unavailable_reason) }}
            </p>
            <p v-else class="refund-policy-note">{{ refundFeePolicyMessage(refundForm.refund_policy) }}</p>
          </template>
          <div v-else class="refund-quote-placeholder">Loading the actual Stripe fee and refundable amount…</div>
        </section>

        <el-form label-width="120px" size="small" class="refund-form">
          <el-form-item label="Admin note">
            <el-input
              v-model="refundForm.admin_note"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              placeholder="Reason for Stripe refund."
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer">
        <el-button @click="refundVisible = false">Cancel</el-button>
        <el-button
          type="danger"
          :loading="refundLoading"
          :disabled="refundSubmitDisabled"
          @click="submitRefund"
        >
          {{ refundButtonLabel }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Rollback Only"
      :visible.sync="rollbackVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <div v-if="rollbackTarget">
        <el-alert
          title="This action only rolls local access back. It does not refund money or cancel the Stripe subscription."
          type="warning"
          :closable="false"
          show-icon
          class="refund-alert"
        />

        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="Invoice">
            <span class="mono">{{ rollbackTarget.stripe_invoice_id }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Amount">{{ formatMoney(rollbackTarget.usd_price, rollbackTarget.currency) }}</el-descriptions-item>
          <el-descriptions-item label="Current access until">{{ formatDate(activeSubscription && activeSubscription.end_date) }}</el-descriptions-item>
          <el-descriptions-item label="Rollback access to">{{ formatDate(rollbackTarget.refund_restore_end_date || rollbackTarget.period_start) }}</el-descriptions-item>
        </el-descriptions>

        <el-form label-width="120px" size="small" class="refund-form">
          <el-form-item label="Admin note">
            <el-input
              v-model="rollbackForm.admin_note"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              placeholder="Reason for rolling back local access."
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer">
        <el-button @click="rollbackVisible = false">Cancel</el-button>
        <el-button type="warning" :loading="rollbackLoading" @click="submitRollback">Confirm Rollback Only</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Cancel Future Renewal"
      :visible.sync="cancelVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div v-if="cancelTarget">
        <el-alert
          title="This action does not refund or roll back access. It only stops the next automatic renewal."
          type="warning"
          :closable="false"
          show-icon
          class="refund-alert"
        />

        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="User">
            {{ cancelTarget.user_email || (historyUser && historyUser.user_email) || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="Stripe subscription">
            <span class="mono">{{ cancelTarget.stripe_subscription_id || '-' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Current access until">{{ formatDate(cancelTarget.end_date) }}</el-descriptions-item>
          <el-descriptions-item label="Auto renew">
            <el-tag size="small" :type="cancelTarget.auto_renew ? 'success' : 'info'">
              {{ cancelTarget.auto_renew ? 'Enabled' : 'Off' }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <el-form label-width="120px" size="small" class="refund-form">
          <el-form-item label="Admin note">
            <el-input
              v-model="cancelForm.admin_note"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              placeholder="Reason for stopping future renewal."
            />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer">
        <el-button @click="cancelVisible = false">Cancel</el-button>
        <el-button type="warning" :loading="cancelLoading" @click="submitCancelRenewal">Confirm Cancel Renewal</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  cancelRefundAndRollbackPlatformSubscription,
  cancelPlatformSubscriptionRenewal,
  getPlatformSubscriptionHistoryForAdmin,
  listUserReadingActivity,
  listPlatformSubscriptions,
  previewPlatformSubscriptionRefund,
  refreshPlatformSubscriptionPaymentIdentity,
  refundPlatformSubscriptionInvoice,
  rollbackPlatformSubscriptionInvoice,
  voidPlatformSubscriptionInvoice
} from '@/apis/lfAdmin'

export default {
  name: 'PlatformBilling',
  computed: {
    browserTimeZoneLabel() {
      let zone = 'Browser local time'
      try {
        zone = Intl.DateTimeFormat().resolvedOptions().timeZone || zone
      } catch (e) {
        // Older browsers still get the numeric UTC offset below.
      }
      return `${zone} (${this.formatUtcOffset()})`
    },
    combinedOpenInvoices() {
      if (!this.combinedTarget) return []
      const targetSubscriptionId = this.combinedTarget.platform_subscription_id || this.extractId(this.selectedSubscription)
      return (this.history || []).filter(item => (
        item &&
        item.source === 'stripe_invoice' &&
        String(item.status || '').toUpperCase() === 'OPEN' &&
        (!targetSubscriptionId || item.platform_subscription_id === targetSubscriptionId)
      ))
    },
    refundSubmitDisabled() {
      if (this.refundLoading || this.refundQuoteLoading || !this.refundQuote || this.refundQuoteError) return true
      return this.refundForm.refund_policy === 'deduct_stripe_fee' && !this.refundQuote.fee_deduction_available
    },
    refundButtonLabel() {
      if (this.refundQuoteLoading) return 'Calculating Refund…'
      if (!this.refundQuote) return 'Confirm Refund Only'
      return `Refund ${this.formatCents(
        this.refundAmountForPolicy(this.refundQuote, this.refundForm.refund_policy),
        this.refundQuote.currency
      )}`
    },
    combinedSubmitDisabled() {
      if (this.combinedUsageLoading || this.combinedLoading) return true
      if (!this.combinedTarget || !this.combinedTarget.can_refund) return false
      if (this.combinedRefundQuoteLoading || !this.combinedRefundQuote || this.combinedRefundQuoteError) return true
      return this.combinedForm.refund_policy === 'deduct_stripe_fee' && !this.combinedRefundQuote.fee_deduction_available
    },
    combinedActionButtonLabel() {
      if (this.combinedUsageLoading) return 'Checking Usage…'
      if (this.combinedTarget && this.combinedTarget.can_refund && this.combinedRefundQuoteLoading) {
        return 'Calculating Refund…'
      }
      if (this.combinedTarget && this.combinedTarget.can_refund && this.combinedRefundQuote) {
        const amount = this.refundAmountForPolicy(this.combinedRefundQuote, this.combinedForm.refund_policy)
        return `Confirm 4 Steps · Refund ${this.formatCents(amount, this.combinedRefundQuote.currency)}`
      }
      return 'Confirm 4-Step Action'
    }
  },
  data() {
    return {
      identityVisible: false,
      identityLoading: false,
      identityTarget: null,
      identityResult: null,
      identityError: '',
      subscriptionLoading: false,
      historyLoading: false,
      refundLoading: false,
      voidLoading: false,
      rollbackLoading: false,
      cancelLoading: false,
      combinedLoading: false,
      combinedUsageLoading: false,
      combinedRefundQuoteLoading: false,
      refundEligibilityLoading: false,
      subscriptions: [],
      total: 0,
      page: 1,
      pageSize: 10,
      filters: {
        search: ''
      },
      selectedSubscription: null,
      historyVisible: false,
      historyUser: null,
      activeSubscription: null,
      billingSubscription: null,
      history: [],
      rollbackCandidate: null,
      combinedCandidate: null,
      combinedVisible: false,
      combinedTarget: null,
      combinedUsage: null,
      combinedUsageError: '',
      combinedRefundQuote: null,
      combinedRefundQuoteError: '',
      combinedForm: {
        refund_policy: 'full',
        admin_note: ''
      },
      refundEligibilityVisible: false,
      refundEligibilityTarget: null,
      refundEligibilityUsage: null,
      refundEligibilityError: '',
      refundVisible: false,
      refundTarget: null,
      refundQuoteLoading: false,
      refundQuote: null,
      refundQuoteError: '',
      refundForm: {
        refund_policy: 'full',
        admin_note: ''
      },
      voidVisible: false,
      voidTarget: null,
      voidForm: {
        admin_note: ''
      },
      rollbackVisible: false,
      rollbackTarget: null,
      rollbackForm: {
        admin_note: ''
      },
      cancelVisible: false,
      cancelTarget: null,
      cancelForm: {
        admin_note: ''
      }
    }
  },
  mounted() {
    this.fetchSubscriptions()
  },
  methods: {
    openIdentityCheck(row) {
      this.identityTarget = row
      this.identityResult = null
      this.identityError = ''
      this.identityVisible = true
      this.checkIdentity(false)
    },
    async checkIdentity(apply) {
      if (this.identityLoading || !this.identityTarget) return
      this.identityLoading = true
      this.identityError = ''
      try {
        const response = await refreshPlatformSubscriptionPaymentIdentity({
          platform_subscription_id: this.extractId(this.identityTarget), apply
        })
        const result = response.data
        if (!result || result.code !== 0) throw new Error(result && result.msg || 'Unable to check current Stripe details')
        this.identityResult = result.data
        if (apply) {
          this.$message.success('Current Stripe details and access check saved')
          await this.fetchSubscriptions()
        }
      } catch (error) {
        this.identityResult = null
        this.identityError = error.message || 'Unable to check current Stripe details'
      } finally {
        this.identityLoading = false
      }
    },
    refresh() {
      this.fetchSubscriptions()
      if (this.historyUser) this.loadHistory(this.historyUser._id)
    },
    search() {
      this.page = 1
      this.fetchSubscriptions()
    },
    async fetchSubscriptions() {
      this.subscriptionLoading = true
      try {
        const res = await listPlatformSubscriptions({
          search: this.filters.search,
          page: this.page,
          page_size: this.pageSize
        })
        if (res.data.code === 0) {
          this.subscriptions = (res.data.data && res.data.data.items) || []
          this.total = (res.data.data && res.data.data.total) || 0
        } else {
          this.$message.error(res.data.msg || 'Failed to load subscriptions')
        }
      } catch (e) {
        this.$message.error('Failed to load subscriptions')
      } finally {
        this.subscriptionLoading = false
      }
    },
    selectSubscription(row) {
      this.selectedSubscription = row
      const userId = row && row.user_id
      if (userId) {
        this.historyVisible = true
        this.loadHistory(userId)
      }
    },
    async loadHistory(userId) {
      if (!userId) return
      this.historyLoading = true
      this.rollbackCandidate = null
      this.combinedCandidate = null
      try {
        const res = await getPlatformSubscriptionHistoryForAdmin({ user_id: userId })
        if (res.data.code === 0) {
          const data = res.data.data || {}
          this.historyUser = data.user || null
          this.activeSubscription = data.active_subscription || null
          this.billingSubscription = data.billing_subscription || data.active_subscription || null
          this.history = data.history || []
          this.rollbackCandidate = this.findRollbackCandidate(this.history)
          this.combinedCandidate = this.findCombinedCandidate(this.history)
        } else {
          this.$message.error(res.data.msg || 'Failed to load billing history')
          this.billingSubscription = null
          this.rollbackCandidate = null
          this.combinedCandidate = null
        }
      } catch (e) {
        this.$message.error('Failed to load billing history')
        this.billingSubscription = null
        this.rollbackCandidate = null
        this.combinedCandidate = null
      } finally {
        this.historyLoading = false
      }
    },
    findRollbackCandidate(history) {
      return (history || []).find(item => item && item.can_rollback) || null
    },
    findCombinedCandidate(history) {
      return (history || []).find(item => (
        item &&
        item.source === 'stripe_invoice' &&
        item.stripe_invoice_id &&
        (item.can_refund || item.can_rollback) &&
        !(item.refunded && item.rolled_back)
      )) || null
    },
    canRunCombinedAction(row) {
      if (!row || !row.stripe_invoice_id || (!row.can_refund && !row.can_rollback)) return false
      if (!this.activeSubscription) return false
      const activeId = this.extractId(this.activeSubscription)
      const targetId = row.platform_subscription_id || this.extractId(this.selectedSubscription)
      if (activeId && targetId && activeId !== targetId) return false
      return this.canCancelRenewal(this.activeSubscription) || !this.activeSubscription.auto_renew
    },
    openCombinedAction(row) {
      if (!this.canRunCombinedAction(row)) return
      this.combinedTarget = row
      this.combinedUsage = null
      this.combinedUsageError = ''
      this.combinedRefundQuote = null
      this.combinedRefundQuoteError = ''
      this.combinedForm = {
        refund_policy: 'full',
        admin_note: ''
      }
      this.combinedVisible = true
      this.loadCombinedUsage()
      if (row.can_refund) this.loadCombinedRefundQuote()
    },
    async fetchRefundQuote(target) {
      if (!target) throw new Error('missing_refund_target')
      const subscriptionId = target.platform_subscription_id || this.extractId(this.selectedSubscription)
      if (!subscriptionId || !target.stripe_invoice_id) throw new Error('missing_refund_identifiers')
      const res = await previewPlatformSubscriptionRefund({
        platform_subscription_id: subscriptionId,
        user_id: target.user_id || (this.historyUser && this.historyUser._id),
        stripe_invoice_id: target.stripe_invoice_id
      })
      if (!res.data || res.data.code !== 0) {
        throw new Error((res.data && res.data.msg) || 'refund_calculation_failed')
      }
      return res.data.data || null
    },
    refundQuoteErrorMessage(error) {
      const code = error && error.message
      const messages = {
        invoice_has_no_refundable_amount: 'Stripe reports that this invoice has no remaining refundable amount.',
        invoice_not_paid: 'Stripe no longer reports this invoice as paid.',
        invoice_subscription_mismatch: 'Safety check failed: this invoice does not belong to the selected subscription.',
        missing_refund_identifiers: 'The invoice or subscription identifier is missing.'
      }
      return messages[code] || 'The Stripe refund calculation could not be loaded. Retry before continuing.'
    },
    async loadCombinedRefundQuote() {
      if (!this.combinedTarget || !this.combinedTarget.can_refund) return
      const invoiceId = this.combinedTarget.stripe_invoice_id
      this.combinedRefundQuoteLoading = true
      this.combinedRefundQuote = null
      this.combinedRefundQuoteError = ''
      try {
        const quote = await this.fetchRefundQuote(this.combinedTarget)
        if (!this.combinedTarget || this.combinedTarget.stripe_invoice_id !== invoiceId) return
        this.combinedRefundQuote = quote
        if (!quote.fee_deduction_available && this.combinedForm.refund_policy === 'deduct_stripe_fee') {
          this.combinedForm.refund_policy = 'full'
        }
      } catch (e) {
        if (!this.combinedTarget || this.combinedTarget.stripe_invoice_id !== invoiceId) return
        this.combinedRefundQuoteError = this.refundQuoteErrorMessage(e)
      } finally {
        if (this.combinedTarget && this.combinedTarget.stripe_invoice_id === invoiceId) {
          this.combinedRefundQuoteLoading = false
        }
      }
    },
    async loadCombinedUsage() {
      if (!this.combinedTarget) return
      const invoiceId = this.combinedTarget.stripe_invoice_id
      this.combinedUsageLoading = true
      this.combinedUsage = null
      this.combinedUsageError = ''
      try {
        const result = await this.fetchUsageAfterPayment(this.combinedTarget)
        if (!this.combinedTarget || this.combinedTarget.stripe_invoice_id !== invoiceId) return
        this.combinedUsage = result.usage
        this.combinedUsageError = result.error
      } finally {
        if (this.combinedTarget && this.combinedTarget.stripe_invoice_id === invoiceId) {
          this.combinedUsageLoading = false
        }
      }
    },
    async fetchUsageAfterPayment(target) {
      const chargedAt = Number(
        this.getPaymentEventAt(target) ||
        this.getInvoiceCreatedAt(target) ||
        target.period_start ||
        0
      )
      const userId = target.user_id || (this.historyUser && this.historyUser._id) || ''
      if (!chargedAt || !userId) {
        return {
          usage: null,
          error: 'Payment time or student id is unavailable, so usage could not be checked.'
        }
      }

      try {
        const res = await listUserReadingActivity({
          search: userId,
          date_from: dayjs.unix(chargedAt).format('YYYY-MM-DD'),
          date_to: dayjs().format('YYYY-MM-DD'),
          page: 1,
          page_size: 100
        })
        if (res.data.code !== 0) {
          return {
            usage: null,
            error: res.data.msg || 'LearningFirst usage could not be checked.'
          }
        }

        const data = res.data.data || {}
        const returnedItems = data.items || []
        const items = returnedItems.filter(item => (
          String(item.user_id || '') === String(userId) &&
          Number(item.read_time || 0) >= chargedAt
        ))
        const oldestReturnedAt = returnedItems.reduce((oldest, item) => {
          const readTime = Number(item.read_time || 0)
          if (!readTime) return oldest
          return oldest === 0 ? readTime : Math.min(oldest, readTime)
        }, 0)
        const total = Number(data.total || returnedItems.length)
        const countIsMinimum = total > returnedItems.length && oldestReturnedAt >= chargedAt
        const latestUsedAt = items.reduce((latest, item) => Math.max(latest, Number(item.read_time || 0)), 0)

        return {
          usage: {
            has_usage: items.length > 0,
            charged_at: chargedAt,
            read_event_count: items.length,
            count_is_minimum: countIsMinimum,
            latest_used_at: latestUsedAt,
            recent_activity: items.slice(0, 5)
          },
          error: ''
        }
      } catch (e) {
        return {
          usage: null,
          error: 'LearningFirst usage could not be checked.'
        }
      }
    },
    canCheckRefundEligibility(row) {
      const userId = row && (row.user_id || (this.historyUser && this.historyUser._id))
      return Boolean(
        row &&
        row.source === 'stripe_invoice' &&
        row.stripe_invoice_id &&
        this.getPaymentEventAt(row) &&
        userId
      )
    },
    openRefundEligibility(row) {
      if (!this.canCheckRefundEligibility(row)) return
      this.refundEligibilityTarget = row
      this.refundEligibilityUsage = null
      this.refundEligibilityError = ''
      this.refundEligibilityVisible = true
      this.loadRefundEligibility()
    },
    async loadRefundEligibility() {
      if (!this.refundEligibilityTarget) return
      const invoiceId = this.refundEligibilityTarget.stripe_invoice_id
      this.refundEligibilityLoading = true
      this.refundEligibilityUsage = null
      this.refundEligibilityError = ''
      try {
        const result = await this.fetchUsageAfterPayment(this.refundEligibilityTarget)
        if (!this.refundEligibilityTarget || this.refundEligibilityTarget.stripe_invoice_id !== invoiceId) return
        this.refundEligibilityUsage = result.usage
        this.refundEligibilityError = result.error
      } finally {
        if (this.refundEligibilityTarget && this.refundEligibilityTarget.stripe_invoice_id === invoiceId) {
          this.refundEligibilityLoading = false
        }
      }
    },
    resetRefundEligibilityCheck() {
      this.refundEligibilityTarget = null
      this.refundEligibilityUsage = null
      this.refundEligibilityError = ''
      this.refundEligibilityLoading = false
    },
    usageActivitySummary(usage) {
      const count = usage.count_is_minimum ? `At least ${usage.read_event_count}` : `${usage.read_event_count}`
      const latest = this.formatDate(usage.latest_used_at, true)
      return `${count} reading event${usage.read_event_count === 1 ? '' : 's'} detected after payment. Latest activity: ${latest}`
    },
    usageAlertDescription(usage) {
      return `${this.usageActivitySummary(usage)}. Review this activity before confirming cancellation, invoice voiding, refund, and access rollback.`
    },
    refundEligibilityDescription(usage) {
      return `${this.usageActivitySummary(usage)}. Review this activity before deciding whether to refund.`
    },
    openRefund(row) {
      if (!row || !row.can_refund) return
      this.refundTarget = row
      this.refundQuote = null
      this.refundQuoteError = ''
      this.refundForm = {
        refund_policy: 'full',
        admin_note: ''
      }
      this.refundVisible = true
      this.loadRefundQuote()
    },
    async loadRefundQuote() {
      if (!this.refundTarget) return
      const invoiceId = this.refundTarget.stripe_invoice_id
      this.refundQuoteLoading = true
      this.refundQuote = null
      this.refundQuoteError = ''
      try {
        const quote = await this.fetchRefundQuote(this.refundTarget)
        if (!this.refundTarget || this.refundTarget.stripe_invoice_id !== invoiceId) return
        this.refundQuote = quote
        if (!quote.fee_deduction_available && this.refundForm.refund_policy === 'deduct_stripe_fee') {
          this.refundForm.refund_policy = 'full'
        }
      } catch (e) {
        if (!this.refundTarget || this.refundTarget.stripe_invoice_id !== invoiceId) return
        this.refundQuoteError = this.refundQuoteErrorMessage(e)
      } finally {
        if (this.refundTarget && this.refundTarget.stripe_invoice_id === invoiceId) {
          this.refundQuoteLoading = false
        }
      }
    },
    canVoidInvoice(row) {
      return Boolean(row && row.can_void && row.stripe_invoice_id)
    },
    isVoidedInvoice(row) {
      return String((row && row.status) || '').toUpperCase() === 'VOID'
    },
    openVoidInvoice(row) {
      if (!this.canVoidInvoice(row)) return
      this.voidTarget = row
      this.voidForm = {
        admin_note: ''
      }
      this.voidVisible = true
    },
    async submitVoidInvoice() {
      if (!this.voidTarget) return
      const subscriptionId = this.voidTarget.platform_subscription_id || this.extractId(this.selectedSubscription)
      if (!subscriptionId || !this.voidTarget.stripe_invoice_id) return
      const confirmed = await this.confirmAdminAction(
        'Permanently void this unpaid Stripe invoice? Stripe will stop retrying it, and the hosted invoice can no longer be paid. This does not cancel future subscription renewals.',
        'Confirm Void Invoice',
        'Void Invoice',
        'error'
      )
      if (!confirmed) return
      this.voidLoading = true
      try {
        const res = await voidPlatformSubscriptionInvoice({
          platform_subscription_id: subscriptionId,
          user_id: this.voidTarget.user_id || (this.historyUser && this.historyUser._id),
          stripe_invoice_id: this.voidTarget.stripe_invoice_id,
          admin_note: this.voidForm.admin_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          const alreadyVoid = res.data.data && res.data.data.already_void
          this.$message.success(alreadyVoid
            ? 'Invoice was already void. No payment retry remains.'
            : 'Invoice voided. Stripe payment retries have stopped.')
          this.voidVisible = false
          this.fetchSubscriptions()
          if (this.historyUser) await this.loadHistory(this.historyUser._id)
        } else {
          this.$message.error(this.voidInvoiceFailureMessage(res.data))
        }
      } catch (e) {
        this.$message.error('Unable to verify whether the invoice was voided. Reload history before retrying.')
      } finally {
        this.voidLoading = false
      }
    },
    voidInvoiceFailureMessage(response) {
      const msg = response && response.msg
      const messages = {
        only_open_invoice_can_be_voided: 'This invoice is no longer open. Reload billing history to see its latest Stripe status.',
        paid_invoice_cannot_be_voided: 'This invoice has a payment and cannot be voided. Review it for a refund instead.',
        invoice_subscription_mismatch: 'Safety check failed: the invoice does not belong to this Stripe subscription.',
        stripe_invoice_void_not_confirmed: 'Stripe did not confirm the invoice as void. Reload history before retrying.'
      }
      return messages[msg] || msg || 'Invoice void failed'
    },
    openRollback(row) {
      if (!row || !row.can_rollback) return
      this.rollbackTarget = row
      this.rollbackForm = {
        admin_note: ''
      }
      this.rollbackVisible = true
    },
    canCancelRenewal(row) {
      if (!row) return false
      const endDate = Number(row.end_date || 0)
      return Boolean(
        row.stripe_subscription_id &&
        row.auto_renew &&
        row.status === 'ACTIVE' &&
        endDate > Math.floor(Date.now() / 1000)
      )
    },
    openCancelRenewal(row) {
      if (!this.canCancelRenewal(row)) return
      this.cancelTarget = row
      this.cancelForm = {
        admin_note: ''
      }
      this.cancelVisible = true
    },
    async submitRefund() {
      if (!this.refundTarget || !this.selectedSubscription) return
      if (this.refundSubmitDisabled) return
      const subscriptionId = this.refundTarget.platform_subscription_id || this.extractId(this.selectedSubscription)
      if (!subscriptionId || !this.refundTarget.stripe_invoice_id) return
      const refundAmount = this.formatCents(
        this.refundAmountForPolicy(this.refundQuote, this.refundForm.refund_policy),
        this.refundQuote.currency
      )
      const feeTreatment = this.refundForm.refund_policy === 'deduct_stripe_fee'
        ? 'The Stripe processing fee will be deducted from the refund.'
        : 'The Stripe processing fee will also be refunded.'
      const confirmed = await this.confirmAdminAction(
        `Confirm a ${refundAmount} Stripe refund? ${feeTreatment} This will not cancel renewal or change local access.`,
        'Confirm Refund Only',
        `Refund ${refundAmount}`,
        'warning'
      )
      if (!confirmed) return
      this.refundLoading = true
      try {
        const res = await refundPlatformSubscriptionInvoice({
          platform_subscription_id: subscriptionId,
          user_id: this.refundTarget.user_id || (this.historyUser && this.historyUser._id),
          stripe_invoice_id: this.refundTarget.stripe_invoice_id,
          refund_policy: this.refundForm.refund_policy,
          admin_note: this.refundForm.admin_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          const log = res.data.data && res.data.data.refund_log
          const actualAmount = log
            ? this.formatCents(log.amount_refunded, log.currency || this.refundQuote.currency)
            : refundAmount
          this.$message.success(`${actualAmount} Stripe refund submitted. Subscription access was not changed.`)
          this.refundVisible = false
          this.fetchSubscriptions()
          if (this.historyUser) this.loadHistory(this.historyUser._id)
        } else {
          this.$message.error(this.refundFailureMessage(res.data))
        }
      } catch (e) {
        this.$message.error('Refund failed')
      } finally {
        this.refundLoading = false
      }
    },
    refundFailureMessage(response) {
      const msg = response && response.msg
      const messages = {
        stripe_charge_unavailable: 'Stripe could not resolve the payment charge, so the processing fee cannot be deducted safely.',
        stripe_processing_fee_unavailable: 'Stripe did not return a verifiable processing fee. Reload the calculation or use a full refund.',
        stripe_processing_fee_currency_mismatch: 'Stripe did not return a usable conversion for the settlement currency.',
        stripe_processing_fee_conversion_unavailable: 'Stripe did not return a usable conversion for the settlement currency.',
        stripe_fee_deducted_refund_not_positive: 'The fee-deducted refund amount is not positive.',
        invoice_has_no_refundable_amount: 'Stripe reports that no refundable amount remains for this invoice.',
        invoice_already_refunded_or_rolled_back: 'This invoice already has a recorded refund. Reload billing history before continuing.',
        invalid_refund_policy: 'The selected fee deduction option is invalid. Reload the page and try again.'
      }
      return messages[msg] || msg || 'Refund failed'
    },
    async submitRollback() {
      if (!this.rollbackTarget || !this.selectedSubscription) return
      const subscriptionId = this.rollbackTarget.platform_subscription_id || this.extractId(this.selectedSubscription)
      if (!subscriptionId || !this.rollbackTarget.stripe_invoice_id) return
      const confirmed = await this.confirmAdminAction(
        'Confirm rollback only? This will roll local access back. It will not refund money or cancel the Stripe subscription.',
        'Confirm Rollback Only',
        'Confirm Rollback Only',
        'warning'
      )
      if (!confirmed) return
      this.rollbackLoading = true
      try {
        const res = await rollbackPlatformSubscriptionInvoice({
          platform_subscription_id: subscriptionId,
          user_id: this.rollbackTarget.user_id || (this.historyUser && this.historyUser._id),
          stripe_invoice_id: this.rollbackTarget.stripe_invoice_id,
          admin_note: this.rollbackForm.admin_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          this.$message.success('Local access rolled back. Stripe refund and subscription were not changed.')
          this.rollbackVisible = false
          this.fetchSubscriptions()
          if (this.historyUser) this.loadHistory(this.historyUser._id)
        } else {
          this.$message.error(res.data.msg || 'Rollback failed')
        }
      } catch (e) {
        this.$message.error('Rollback failed')
      } finally {
        this.rollbackLoading = false
      }
    },
    async submitCancelRenewal() {
      if (!this.cancelTarget) return
      const subscriptionId = this.extractId(this.cancelTarget)
      if (!subscriptionId) return
      const confirmed = await this.confirmAdminAction(
        'Confirm cancel only? This will stop future auto-renewal and keep current access until the existing end date.',
        'Confirm Cancel Only',
        'Confirm Cancel Only',
        'warning'
      )
      if (!confirmed) return
      this.cancelLoading = true
      try {
        const res = await cancelPlatformSubscriptionRenewal({
          platform_subscription_id: subscriptionId,
          user_id: this.cancelTarget.user_id || (this.historyUser && this.historyUser._id),
          admin_note: this.cancelForm.admin_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          this.$message.success('Future auto-renewal stopped. Access remains until the current end date.')
          this.cancelVisible = false
          this.fetchSubscriptions()
          if (this.historyUser) this.loadHistory(this.historyUser._id)
        } else {
          this.$message.error(res.data.msg || 'Cancel renewal failed')
        }
      } catch (e) {
        this.$message.error('Cancel renewal failed')
      } finally {
        this.cancelLoading = false
      }
    },
    async submitCombinedAction() {
      if (!this.combinedTarget || !this.selectedSubscription) return
      if (this.combinedSubmitDisabled) return
      const subscriptionId = this.combinedTarget.platform_subscription_id || this.extractId(this.selectedSubscription)
      if (!subscriptionId || !this.combinedTarget.stripe_invoice_id) return
      this.combinedLoading = true
      try {
        const res = await cancelRefundAndRollbackPlatformSubscription({
          platform_subscription_id: subscriptionId,
          user_id: this.combinedTarget.user_id || (this.historyUser && this.historyUser._id),
          stripe_invoice_id: this.combinedTarget.stripe_invoice_id,
          refund_policy: this.combinedForm.refund_policy,
          admin_note: this.combinedForm.admin_note,
          reviewer_name: 'LF Admin'
        })
        if (res.data.code === 0) {
          const emailStatus = res.data.data && res.data.data.customer_email_status
          if (emailStatus === 'failed') {
            this.$message.warning('All 4 billing actions completed, but the customer notification email failed.')
          } else {
            const refundLog = res.data.data && res.data.data.refund_log
            const refundAmount = refundLog
              ? this.formatCents(refundLog.amount_refunded, refundLog.currency)
              : 'Selected invoice'
            this.$message.success(`Renewal cancelled, OPEN invoices voided, ${refundAmount} refunded, and access date rolled back.`)
          }
          this.combinedVisible = false
        } else {
          this.combinedVisible = false
          this.$alert(
            this.combinedFailureMessage(res.data),
            'Combined action not completed',
            {
              type: 'error',
              confirmButtonText: 'OK'
            }
          ).catch(() => {})
        }
        this.fetchSubscriptions()
        if (this.historyUser) await this.loadHistory(this.historyUser._id)
      } catch (e) {
        this.combinedVisible = false
        this.$alert(
          'The request result is unknown because the server could not be reached. Reload billing history before retrying; completed steps will be detected and skipped safely.',
          'Unable to verify combined action',
          {
            type: 'error',
            confirmButtonText: 'OK'
          }
        ).catch(() => {})
        this.fetchSubscriptions()
        if (this.historyUser) await this.loadHistory(this.historyUser._id)
      } finally {
        this.combinedLoading = false
      }
    },
    combinedFailureMessage(response) {
      const data = (response && response.data) || {}
      const steps = data.steps || {}
      const labels = {
        cancel_renewal: 'Cancel renewal',
        void_open_invoices: 'Void OPEN invoices',
        refund_invoice: 'Refund invoice',
        rollback_access: 'Roll back access'
      }
      const results = Object.keys(labels).map((key) => {
        const step = steps[key] || {}
        return `${labels[key]}: ${step.status || 'not started'}`
      })
      const failedLabel = labels[data.failed_step] || data.failed_step || 'preflight'
      const voidInvoices = (steps.void_open_invoices && steps.void_open_invoices.invoices) || []
      const failedVoidInvoice = voidInvoices.find(invoice => invoice && invoice.status === 'failed')
      const failedInvoiceDetail = failedVoidInvoice
        ? ` Failed invoice: ${failedVoidInvoice.stripe_invoice_id}.`
        : ''
      const serverMessage = response && response.msg
      const refundMessages = {
        stripe_charge_unavailable: 'Stripe could not resolve the payment charge for fee calculation',
        stripe_processing_fee_unavailable: 'Stripe did not return a verifiable processing fee; reload or select a full refund',
        stripe_processing_fee_currency_mismatch: 'Stripe did not return a usable settlement-currency conversion',
        stripe_processing_fee_conversion_unavailable: 'Stripe did not return a usable settlement-currency conversion',
        stripe_fee_deducted_refund_not_positive: 'The fee-deducted refund amount is not positive'
      }
      const detailMessage = refundMessages[serverMessage] || serverMessage
      const detail = detailMessage ? ` Server response: ${detailMessage}.` : ''
      return `Stopped at ${failedLabel}. ${results.join('; ')}.${failedInvoiceDetail}${detail} Reloaded state can be retried safely; completed steps will be skipped.`
    },
    async confirmAdminAction(message, title, confirmButtonText, type = 'warning') {
      try {
        await this.$confirm(message, title, {
          confirmButtonText,
          cancelButtonText: 'Cancel',
          type,
          closeOnClickModal: false,
          closeOnPressEscape: false
        })
        return true
      } catch (e) {
        return false
      }
    },
    subscriptionRowClass({ row }) {
      if (!this.selectedSubscription || !row) return ''
      return this.extractId(row) === this.extractId(this.selectedSubscription) ? 'selected-row' : ''
    },
    statusTagType(status) {
      if (status === 'ACTIVE') return 'success'
      if (status === 'CANCELLED') return 'warning'
      if (status === 'EXPIRED') return 'info'
      return ''
    },
    formatDate(ts, includeSeconds = false) {
      const value = Number(ts || 0)
      if (!value) return '-'
      return dayjs.unix(value).format(includeSeconds ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm')
    },
    formatUtcDate(ts) {
      const value = Number(ts || 0)
      if (!value) return '-'
      return `${new Date(value * 1000).toISOString().slice(0, 19).replace('T', ' ')} UTC`
    },
    formatUtcOffset() {
      const offsetMinutes = -new Date().getTimezoneOffset()
      const sign = offsetMinutes >= 0 ? '+' : '-'
      const absoluteMinutes = Math.abs(offsetMinutes)
      const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0')
      const minutes = String(absoluteMinutes % 60).padStart(2, '0')
      return `UTC${sign}${hours}:${minutes}`
    },
    getInvoiceCreatedAt(row) {
      if (!row) return null
      return row.invoice_created_at || row.create_time || null
    },
    getPaymentEventAt(row) {
      if (!row) return null
      if (row.payment_succeeded_at) return row.payment_succeeded_at
      if (row.status === 'AWARDED') return row.create_time || null
      return null
    },
    getInvoiceStatusEventAt(row) {
      if (!row) return null
      return this.getPaymentEventAt(row) || row.invoice_voided_at || null
    },
    paymentStatusLabel(status) {
      const normalized = String(status || 'UNKNOWN').toUpperCase()
      const labels = {
        PAID: 'Paid',
        AWARDED: 'Awarded',
        OPEN: 'Payment due',
        UNPAID: 'Unpaid',
        PAST_DUE: 'Past due',
        FAILED: 'Failed',
        VOID: 'Void',
        UNCOLLECTIBLE: 'Uncollectible'
      }
      return labels[normalized] || normalized
    },
    paymentStatusTagType(status) {
      const normalized = String(status || '').toUpperCase()
      if (normalized === 'PAID') return 'success'
      if (normalized === 'AWARDED') return 'info'
      if (['OPEN', 'UNPAID', 'PAST_DUE', 'FAILED'].includes(normalized)) return 'danger'
      return 'info'
    },
    paymentAttemptLabel(row) {
      const attempts = Number(row && row.payment_attempt_count ? row.payment_attempt_count : 0)
      if (!attempts) return ''
      return `${attempts} ${attempts === 1 ? 'attempt' : 'attempts'}`
    },
    formatMoney(amount, currency) {
      const value = Number(amount || 0)
      const code = (currency || 'USD').toUpperCase()
      return `${code} ${value.toFixed(2)}`
    },
    formatCents(amountCents, currency) {
      return this.formatMoney(Number(amountCents || 0) / 100, currency)
    },
    formatOptionalCents(amountCents, currency) {
      if (amountCents === null || typeof amountCents === 'undefined') return 'Unavailable'
      return this.formatCents(amountCents, currency)
    },
    refundAmountForPolicy(quote, policy) {
      if (!quote) return 0
      return policy === 'deduct_stripe_fee'
        ? Number(quote.fee_deducted_refund_amount_cents || 0)
        : Number(quote.full_refund_amount_cents || 0)
    },
    refundFeePolicyMessage(policy) {
      return policy === 'deduct_stripe_fee'
        ? "Stripe's verified processing fee will be subtracted from the refund."
        : 'The full remaining payment will be refunded without deducting the processing fee.'
    },
    refundFeeUnavailableMessage(reason) {
      const messages = {
        stripe_charge_unavailable: 'the payment charge could not be resolved.',
        stripe_processing_fee_unavailable: 'Stripe has not returned a verifiable processing fee.',
        stripe_processing_fee_currency_mismatch: 'Stripe did not return a usable settlement-currency conversion.',
        stripe_processing_fee_conversion_unavailable: 'Stripe did not return a usable settlement-currency conversion.',
        stripe_fee_deducted_refund_not_positive: 'the calculated refund would not be positive.'
      }
      return messages[reason] || 'Stripe did not return a safe fee calculation.'
    },
    existingRefundDescription(row) {
      const log = row && row.refund_log
      if (!log) return 'A retry will skip the refund step and continue with any unfinished operations.'
      const amount = this.formatCents(log.amount_refunded, log.currency || row.currency)
      const feeTreatment = log.refund_policy === 'deduct_stripe_fee'
        ? 'Stripe fee deducted'
        : 'processing fee refunded'
      return `${amount} was already submitted (${feeTreatment}). A retry will skip this step.`
    },
    refundStateLabel(row) {
      const log = row && row.refund_log
      if (!log) return 'Refunded'
      const refunded = Number(log.amount_refunded || 0)
      const paid = Number(log.invoice_amount_paid || 0)
      return paid > 0 && refunded > 0 && refunded < paid ? 'Partially Refunded' : 'Refunded'
    },
    extractId(obj) {
      if (!obj) return ''
      if (typeof obj === 'string') return obj
      const id = obj._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || (id.toString ? id.toString() : '')
    }
  }
}
</script>

<style scoped>
.platform-billing-container {
  padding: 20px;
}

.header,
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.header h2,
.panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

.subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.filters {
  padding: 14px 16px 4px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.history-panel {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e4e7ed;
}

.time-context {
  display: flex;
  gap: 7px;
  align-items: center;
  padding: 8px 10px;
  margin-bottom: 10px;
  color: #606266;
  font-size: 12px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.time-context i {
  color: #409eff;
}

.history-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.state-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.user-main {
  color: #303133;
  font-weight: 600;
}

.muted {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.tiny {
  font-size: 11px;
}

.time-primary {
  color: #303133;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.time-utc {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.payment-heading {
  display: flex;
  gap: 7px;
  align-items: center;
  margin-bottom: 5px;
}

.attempt-count {
  color: #606266;
  font-size: 11px;
  white-space: nowrap;
}

.payment-detail-label {
  margin-bottom: 3px;
}

.mono {
  font-family: Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.refund-alert {
  margin-bottom: 14px;
}

.refund-form {
  margin-top: 16px;
}

.void-renewal-alert {
  margin-top: 14px;
}

.combined-plan,
.open-invoice-review {
  padding: 14px;
  margin-bottom: 14px;
  background: #fafafa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}

.combined-plan-heading,
.open-invoice-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.combined-plan-heading h4,
.open-invoice-heading h4 {
  margin: 0;
  color: #303133;
  font-size: 14px;
}

.combined-plan-heading p,
.open-invoice-heading p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.combined-plan-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 14px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.combined-plan-list li {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  min-width: 0;
  color: #303133;
  font-size: 12px;
}

.combined-plan-list strong,
.combined-plan-list small {
  display: block;
}

.combined-plan-list small {
  margin-top: 2px;
  overflow: hidden;
  color: #909399;
  font-size: 11px;
  line-height: 1.4;
  text-overflow: ellipsis;
}

.step-number {
  display: inline-flex;
  flex: 0 0 22px;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: #f56c6c;
  font-size: 11px;
  font-weight: 600;
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 50%;
}

.open-invoice-list {
  border-top: 1px solid #ebeef5;
}

.open-invoice-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 9px 0;
  border-bottom: 1px solid #ebeef5;
}

.open-invoice-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.open-invoice-row strong {
  flex: 0 0 auto;
  color: #f56c6c;
  font-size: 12px;
}

.refund-policy-panel {
  min-height: 128px;
  padding: 14px;
  margin-top: 16px;
  background: #f8fbff;
  border: 1px solid #d9ecff;
  border-radius: 6px;
}

.refund-policy-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.refund-policy-heading h4 {
  margin: 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.refund-policy-heading p {
  margin: 4px 0 0;
  color: #606266;
  font-size: 12px;
  line-height: 1.5;
}

.refund-policy-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}

.refund-policy-option {
  width: 100%;
  height: auto;
  min-height: 66px;
  padding: 12px 14px;
  margin: 0 !important;
  line-height: 1.35;
  white-space: normal;
}

.refund-policy-label,
.refund-policy-description {
  display: block;
}

.refund-policy-label {
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.refund-policy-description {
  margin-top: 3px;
  color: #909399;
  font-size: 11px;
}

.refund-calculation {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #d9ecff;
}

.refund-calculation > div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
  color: #606266;
  font-size: 12px;
}

.refund-calculation strong {
  color: #303133;
  font-variant-numeric: tabular-nums;
}

.refund-calculation .refund-total {
  padding-top: 8px;
  margin-top: 4px;
  color: #303133;
  font-size: 14px;
  border-top: 1px solid #d9ecff;
}

.refund-calculation .refund-total strong {
  color: #e6a23c;
  font-size: 16px;
}

.refund-policy-note {
  margin: 10px 0 0;
  color: #606266;
  font-size: 12px;
  line-height: 1.5;
}

.refund-quote-placeholder {
  padding: 20px 0;
  color: #909399;
  font-size: 12px;
  text-align: center;
}

.already-refunded-alert {
  margin-top: 16px;
}

.invoice-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
}

.invoice-actions .el-button + .el-button {
  margin-left: 0;
}

.usage-review {
  min-height: 92px;
  padding: 14px;
  margin-top: 16px;
  background: #fafafa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}

.usage-review-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.usage-review-heading h4 {
  margin: 0;
  color: #303133;
  font-size: 14px;
}

.usage-review-heading p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 12px;
}

.usage-check-placeholder {
  padding: 14px 0;
  color: #909399;
  font-size: 13px;
  text-align: center;
}

.usage-events {
  margin-top: 10px;
  border-top: 1px solid #ebeef5;
}

.usage-event {
  display: grid;
  grid-template-columns: 142px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  color: #606266;
  font-size: 12px;
  border-bottom: 1px solid #ebeef5;
}

.usage-event-time {
  font-variant-numeric: tabular-nums;
}

.usage-event-title {
  overflow: hidden;
  color: #303133;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.usage-event-scene {
  color: #909399;
}

.eligibility-note {
  display: flex;
  gap: 7px;
  align-items: flex-start;
  margin-top: 12px;
  color: #606266;
  font-size: 12px;
  line-height: 1.5;
}

.eligibility-note i {
  margin-top: 2px;
  color: #409eff;
}

.customer-notice {
  display: flex;
  gap: 7px;
  align-items: center;
  margin-top: 12px;
  color: #606266;
  font-size: 12px;
}

.customer-notice i {
  color: #409eff;
}

::v-deep .selected-row > td {
  background: #ecf5ff !important;
}

::v-deep .history-dialog .el-dialog {
  max-width: 1480px;
}

::v-deep .combined-dialog .el-dialog {
  max-width: 700px;
  margin-top: 4vh !important;
}

::v-deep .combined-dialog .el-dialog__body {
  max-height: calc(92vh - 140px);
  overflow-y: auto;
}

::v-deep .refund-policy-option .el-radio__input {
  vertical-align: top;
}

::v-deep .refund-policy-option .el-radio__label {
  display: inline-block;
  width: calc(100% - 24px);
  padding-left: 9px;
  vertical-align: top;
}

@media (max-width: 640px) {
  .refund-policy-options {
    grid-template-columns: 1fr;
  }
}
</style>
